/**
 * videoPlayerEventHandlers.ts
 *
 * Factory that creates event handlers for `VideoPlayer` fw:* events.
 * - Purpose: keep analytics-building and tracker-init logic out of the
 *   React component so `VideoPlayer.tsx` remains an orchestrator only.
 * - Exports: `createVideoPlayerHandlers(ctx)` which returns a map of
 *   fw:* event listeners (start, impression, quit, product-clicked).
 * - Consumers: `apps/bfl-live-commerce-app/libs/src/components/VideoPlayer/VideoPlayer.tsx`
 *
 * The handlers accept a lightweight context (analytics builders,
 * tracker init/destroy, helpers) and call `dataLayer`/CleverTap helpers
 * via the provided builder functions.  This file is intentionally
 * side-effect free and safe to unit-test in isolation.
 */

import { dlElementConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import type { EnrichVideoPlayerCT } from "../livecommerceCleverTap";

type Video = Record<string, unknown>;

export type VideoPlayerHandlerContext = {
  sectionTitle: string;
  videoId: string;
  creatorName?: string;
  eventName?: string;
  enableTracking?: boolean;
  NR_VIDEO_LOG?: string;
  // helpers provided by VideoPlayer
  resolveVideoTitle: (detail?: unknown) => string;
  resolveApiVideoMetaCallback: (detail?: unknown) => {
    videoId?: string;
    contentTitle?: string;
    liveStreamStatus?: string;
  };
  resolveLivestreamStatus: (detail?: unknown) => string;
  initVideoTracker: (el: HTMLElement) => void;
  destroyTracker: (reason: string) => void;
  buildVideoPlayerAnalyticsPayload: (opts: {
    eventType: string;
    ctaText: string;
    sectionTitle: string;
    videoIdFallback: string;
    creatorName?: string;
    eventName?: string;
    video?: Video;
    eventNameKey?: string;
  }) => { data: Record<string, string>; cleverTapObject?: Record<string, string> };
  enrichVideoPlayerCT: EnrichVideoPlayerCT;
};

export function createVideoPlayerHandlers(ctx: VideoPlayerHandlerContext) {
  const {
    sectionTitle,
    videoId,
    creatorName,
    eventName,
    NR_VIDEO_LOG,
    initVideoTracker,
    destroyTracker,
    buildVideoPlayerAnalyticsPayload,
  } = ctx;

  const handleProductClick: EventListener = (event: Event) => {
    const customEvent = event as CustomEvent;
    const detail = (customEvent?.detail || {}) as {
      video?: Video;
      product?: { product_name?: string };
    };
    const { data, cleverTapObject } = buildVideoPlayerAnalyticsPayload({
      eventType: customEvent?.type || "",
      ctaText: detail.product?.product_name || "PRODUCT_CLICK",
      sectionTitle,
      videoIdFallback: videoId,
      creatorName,
      eventName,
      video: detail?.video,
      eventNameKey: dlElementConstants.VIDEO_CLICKED,
    });

    dataLayer("", data, cleverTapObject);
  };

  const handlePlayerQuit: EventListener = () => {
    destroyTracker("fw:player:quit");
  };

  const handleVideoImpression: EventListener = () => {
    try {
      const storyBlock =
        (document.querySelector(`fw-storyblock[name="player-${videoId}"]`) as HTMLElement) || null;
      if (storyBlock) {
        initVideoTracker(storyBlock);
      }
    } catch (error) {
      if (NR_VIDEO_LOG) {
        console.warn(NR_VIDEO_LOG, "storyblock not found on impression", { error, videoId });
      }
    }
  };

  const handleVideoStart: EventListener = () => {
    try {
      const storyBlock =
        (document.querySelector(`fw-storyblock[name="player-${videoId}"]`) as HTMLElement) || null;
      if (storyBlock) {
        initVideoTracker(storyBlock);
      }
    } catch (error) {
      if (NR_VIDEO_LOG) {
        console.warn(NR_VIDEO_LOG, "storyblock not found on video start", { error, videoId });
      }
    }
  };

  return {
    handleVideoStart,
    handleProductClick,
    handleVideoImpression,
    handlePlayerQuit,
  };
}
